/**
 * @fileoverview `<MetricCard>` — tuile de métrique backoffice GNPC.
 *
 * Le dashboard BO-01 manipule des indicateurs financiers et opérationnels :
 * paiements du jour, montant collecté, taux de succès, anomalies STI. Ce
 * composant centralise la hiérarchie typographique, les tons d'alerte et les
 * slots d'icône/action sans utiliser d'ombre.
 */

import type { ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card.tsx';
import { Badge } from './ui/badge.tsx';
import { cn } from '../lib/utils.ts';

export type MetricCardTone = 'neutral' | 'violet' | 'success' | 'info' | 'warning' | 'danger';
export type MetricCardTrendDirection = 'up' | 'down' | 'neutral';

export interface MetricCardTrend {
  /** Valeur courte affichée dans le badge, ex : `+12,5 %`, `-3`, `stable`. */
  value: ReactNode;
  /** Sens de la tendance. Pilote la couleur du badge. */
  direction?: MetricCardTrendDirection;
  /** Icône compacte optionnelle, généralement une tendance ou une alerte. */
  icon?: ReactNode;
  /** Label accessible quand la valeur seule n'est pas suffisante. */
  ariaLabel?: string;
}

export interface MetricCardFooter {
  /** Ligne principale de contexte métier sous la valeur. */
  title: ReactNode;
  /** Détail secondaire : période, SLA, périmètre ou règle de lecture. */
  description?: ReactNode;
  /** Icône de contexte optionnelle. */
  icon?: ReactNode;
}

export interface MetricCardProps {
  /** Libellé métier, ex : `Paiements aujourd'hui`. */
  label: ReactNode;
  /** Valeur principale. Peut être un nombre formaté ou un composant `<Amount>`. */
  value: ReactNode;
  /** Texte secondaire : delta, SLA, périmètre, aide. */
  description?: ReactNode;
  /** Icône Lucide ou élément visuel court. */
  icon?: ReactNode;
  /** Variante de couleur sémantique. */
  tone?: MetricCardTone;
  /** Action courte : lien détail, menu, bouton. */
  action?: ReactNode;
  /** Badge de tendance, affiché dans le coin haut droit. */
  trend?: MetricCardTrend;
  /** Contexte bas de carte, utile pour les dashboards et KPI périodiques. */
  footer?: MetricCardFooter;
  /** Classe CSS complémentaire. */
  className?: string;
}

const TONE_CLASSES: Record<MetricCardTone, string> = {
  neutral: 'bg-card',
  violet: 'bg-violet-100',
  success: 'bg-status-paid/10',
  info: 'bg-quipux-blue-50',
  warning: 'bg-warning/10',
  danger: 'bg-danger/10',
};

const ICON_CLASSES: Record<MetricCardTone, string> = {
  neutral: 'text-white bg-quipux-navy-700',
  violet: 'bg-violet-700 text-white',
  success: 'text-white bg-status-paid',
  info: 'text-white bg-quipux-blue-600',
  warning: 'text-white bg-warning',
  danger: 'text-white bg-danger',
};

/**
 * Valeurs et deltas en nuance 800 (pas la couleur de statut de base) :
 * l'ambre/le vert de base tombent sous 4,5:1 sur fond blanc ou teinté /10 —
 * les nuances 800 garantissent le ratio WCAG AA (voir globals.css).
 */
const VALUE_CLASSES: Record<MetricCardTone, string> = {
  neutral: 'text-quipux-navy-950',
  violet: 'text-violet-800',
  success: 'text-status-paid-800',
  info: 'text-quipux-blue-800',
  warning: 'text-status-pending-800',
  danger: 'text-status-failed-800',
};

const TREND_CLASSES: Record<MetricCardTrendDirection, string> = {
  up: 'border-status-paid/30 bg-status-paid/10 text-status-paid-800',
  down: 'border-danger/30 bg-danger/10 text-status-failed-800',
  neutral: 'border-border bg-muted text-muted-foreground',
};

/**
 * Tuile métrique standard.
 *
 * @param props - Libellé, valeur, ton, icône, description, action et classe CSS.
 * @returns Carte compacte de dashboard sans ombre.
 *
 * @example
 * ```tsx
 * <MetricCard
 *   label="Encaissés non enregistrés"
 *   value={7}
 *   tone="danger"
 *   trend={{ value: '+2', direction: 'down' }}
 *   footer={{ title: 'Traitement requis', description: 'Paiements encaissés non enregistrés.' }}
 * />
 * ```
 */
export function MetricCard({
  label,
  value,
  description,
  icon,
  tone = 'neutral',
  action,
  trend,
  footer,
  className,
}: MetricCardProps): React.JSX.Element {
  return (
    <Card
      className={cn(
        // Hauteur uniforme : les cartes s'étirent à la hauteur de la plus
        // haute de la rangée (grille en `items-stretch`) — rangée de tuiles
        // régulière, demandée par le client.
        '@container/card gap-0 h-full overflow-hidden border-0 py-0',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <CardHeader className="flex flex-col items-stretch gap-1.5 px-4 pb-3 pt-3">
        <div className="flex w-full min-w-0 items-center justify-between gap-3">
          {/* `line-clamp-2` plutôt que `truncate` : les libellés métier
            * longs (« Encaissé non enregistré ») restent lisibles sur
            * 2 lignes au lieu d'être coupés en « Encais… ». */}
          <CardDescription className="min-w-0 line-clamp-2">{label}</CardDescription>

          {trend || icon || action ? (
            <div className="flex shrink-0 items-center gap-2">
              {trend ? (
                <Badge
                  variant="outline"
                  className={cn('whitespace-nowrap', TREND_CLASSES[trend.direction ?? 'neutral'])}
                  aria-label={trend.ariaLabel}
                >
                  {trend.icon ? <span className="[&>svg]:size-3">{trend.icon}</span> : null}
                  {trend.value}
                </Badge>
              ) : null}
              {icon ? (
                <span
                  className={cn(
                    'flex size-7 items-center justify-center rounded-md',
                    ICON_CLASSES[tone],
                  )}
                >
                  <span className="[&>svg]:size-3.5">{icon}</span>
                </span>
              ) : null}
              {action}
            </div>
          ) : null}
        </div>

        <CardTitle
          className={cn(
            '@[250px]/card:text-2xl min-w-0 truncate whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight',
            VALUE_CLASSES[tone],
          )}
        >
          {value}
        </CardTitle>
      </CardHeader>

      {description ? (
        <CardContent className="px-4 pb-3 pt-0">
          <p className="text-muted-foreground text-xs leading-5">{description}</p>
        </CardContent>
      ) : null}

      {footer ? (
        <CardFooter className="flex-col items-start gap-1 px-4 pb-3 pt-0 text-sm">
          <div className="text-quipux-navy-950 line-clamp-1 flex items-center gap-2 text-xs font-medium">
            <span>{footer.title}</span>
            {footer.icon ? <span className="[&>svg]:size-3.5">{footer.icon}</span> : null}
          </div>
          {footer.description ? (
            <div className="text-muted-foreground text-xs leading-4">{footer.description}</div>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  );
}
